import React, { useState, useCallback } from 'react';
import Cropper from 'react-easy-crop';

interface ProfileCropProps {
  onCropComplete: (croppedImage: string) => void;
  initialImage?: string;
}

const aspect = 1;

function createImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new window.Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', error => reject(error));
    image.setAttribute('crossOrigin', 'anonymous');
    image.src = url;
  });
}

async function getCroppedImg(imageSrc: string, crop: any): Promise<string> {
  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  const scaleX = image.naturalWidth / image.width;
  const scaleY = image.naturalHeight / image.height;
  const size = Math.min(crop.width * scaleX, crop.height * scaleY);
  canvas.width = 320;
  canvas.height = 320;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('No 2d context');
  ctx.drawImage(
    image,
    crop.x * scaleX,
    crop.y * scaleY,
    size,
    size,
    0,
    0,
    320,
    320
  );
  return canvas.toDataURL('image/jpeg');
}

const ProfileCrop: React.FC<ProfileCropProps> = ({ onCropComplete, initialImage }) => {
  const [imageSrc, setImageSrc] = useState<string | null>(initialImage || null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<any>(null);
  const [cropping, setCropping] = useState(false);
  const [croppedImage, setCroppedImage] = useState<string | null>(null);

  const onSelectFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const reader = new FileReader();
      reader.addEventListener('load', () => setImageSrc(reader.result as string));
      reader.readAsDataURL(e.target.files[0]);
    }
  };

  const onCropChange = (crop: any) => {
    setCrop(crop);
  };

  const onCropCompleteInternal = useCallback((_: any, croppedAreaPixels: any) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleCrop = async () => {
    if (!imageSrc || !croppedAreaPixels) return;
    setCropping(true);
    const cropped = await getCroppedImg(imageSrc, croppedAreaPixels);
    setCropping(false);
    setCroppedImage(cropped);
    onCropComplete(cropped);
  };

  return (
    <div>
      {!imageSrc && (
        <input type="file" accept="image/*" onChange={onSelectFile} />
      )}
      {imageSrc && !croppedImage && (
        <div style={{ position: 'relative', width: 320, height: 320, background: '#333' }}>
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            aspect={aspect}
            onCropChange={onCropChange}
            onCropComplete={onCropCompleteInternal}
            onZoomChange={setZoom}
          />
        </div>
      )}
      {imageSrc && !croppedImage && (
        <div style={{ marginTop: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
          <input
            type="range"
            min={1}
            max={3}
            step={0.01}
            value={zoom}
            onChange={e => setZoom(Number(e.target.value))}
            style={{ flex: 1, marginRight: 16 }}
          />
          <button
            type="button"
            onClick={handleCrop}
            disabled={cropping}
            style={{
              padding: '8px 18px',
              background: '#2d7a46',
              color: '#fff',
              border: 'none',
              borderRadius: 6,
              fontWeight: 600,
              cursor: cropping ? 'not-allowed' : 'pointer',
              opacity: cropping ? 0.7 : 1
            }}
          >
            {cropping ? 'Recortando...' : 'Usar recorte'}
          </button>
          <button
            type="button"
            onClick={() => setImageSrc(null)}
            style={{
              padding: '8px 18px',
              background: '#eee',
              color: '#222',
              border: '1px solid #bbb',
              borderRadius: 6,
              fontWeight: 600,
              marginLeft: 0,
              cursor: 'pointer',
              transition: 'background 0.2s'
            }}
          >
            Trocar imagem
          </button>
        </div>
      )}
      {croppedImage && (
        <div style={{ marginTop: 16 }}>
          <img src={croppedImage} alt="Prévia perfil recortado" style={{ maxWidth: 160, maxHeight: 160, borderRadius: 8, border: '1px solid #ccc' }} />
          <div style={{ marginTop: 12 }}>
            <button
              type="button"
              onClick={() => {
                setImageSrc(null);
                setCroppedImage(null);
              }}
              style={{
                padding: '8px 18px',
                background: '#eee',
                color: '#222',
                border: '1px solid #bbb',
                borderRadius: 6,
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'background 0.2s'
              }}
            >
              Trocar imagem
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfileCrop; 